import React from 'react'
import ReactDOM from 'react-dom'
import App from './App'
import './reset.scss'

import { mapStackTrace } from 'sourcemapped-stacktrace'

import loadData from 'data/load'
import Store from 'store'
import EditingContext from 'store/editingContext'
import Session from 'store/session'

import { Api } from 'api'
import { getProfilePath, getStoredValue, PD2, PTR } from 'common'
import { exportProfile } from 'components/import/useLoadProfile'
import { stateFix } from 'logic/stateReducers'

const mapStackTraceP = (stack) =>
  new Promise((resolve) => {
    mapStackTrace(stack, (result) => resolve(result))
  })

class ErrorReporter extends React.Component {
  constructor(props) {
    super(props)
    this.state = {}
  }

  static getDerivedStateFromError(error) {
    if (!ErrorReporter.caught) {
      ErrorReporter.caught = true
      return { error: { message: error.message, stack: error.stack } }
    }
  }

  componentDidCatch(error, info) {
    if (!this.mapped) {
      this.mapped = true
      Promise.all([
        mapStackTraceP(error.stack),
        mapStackTraceP(info.componentStack)
      ]).then(([stack, components]) =>
        this.setState((state) => ({
          error: {
            ...state.error,
            stack: stack.join('\n'),
            components: components.join('\n')
          }
        }))
      )
    }
  }

  onSave = () => {
    const state = Store.instance.getState()
    const api = new Api()
    this.setState({ saved: { saving: true } })
    api.save(exportProfile(state, 'Error report'), { error: true }).then(
      (id) => this.setState({ saved: { id } }),
      (error) => this.setState({ saved: { error: error.message } })
    )
  }

  render() {
    if (this.state.error) {
      const {
        saved,
        error: { message, stack, components }
      } = this.state

      return (
        <div className="d2p-ErrorReport">
          <div>
            <h3>Oops! Something broke.</h3>
            <p>
              Please submit a report over at{' '}
              <a
                href="https://discord.gg/WBBwg5X"
                target="_blank"
                rel="noopener noreferrer">
                Maxroll discord
              </a>{' '}
              describing what you were doing, along with the information below.
            </p>
            <p>
              If you believe the profile is relevant, please include a link to
              it as well.
            </p>
            <p>
              {!saved && (
                <span className="d2p-save" onClick={this.onSave}>
                  Save profile
                </span>
              )}
              {!!(saved && saved.saving) && <span>Saving...</span>}
              {!!(saved && saved.id) && (
                <a
                  href={getProfilePath(saved.id)}
                  target="_blank"
                  rel="noopener noreferrer">
                  {new URL(getProfilePath(saved.id), window.location.href).href}
                </a>
              )}
              {!!(saved && saved.error) && (
                <span className="d2p-error">{saved.error}</span>
              )}
            </p>
            <div className="d2p-report">
              The following error has occurred: {message || 'Unknown error'}
              {'\n'}
              {!!stack && stack + '\n'}
              {!!components && 'Components:\n' + components + '\n'}
              User agent: {navigator.userAgent}
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

;(async function () {
  const editingContext = new EditingContext()

  const dataLoaded = loadData(
    PD2 ? 'pd2' : PTR ? 'ptr' : getStoredValue('d2p-gameVersion')
  )

  let loading = null
  const match = window.location.pathname.match(
    /\/d2planner(?:-pd2|-ptr)?\/(\w{8,9})$/
  )
  if (match) {
    const setName =
      window.location.hash &&
      decodeURIComponent(window.location.hash.substring(1))
    loading = new Api().load(match[1]).then(
      async (result) => {
        await dataLoaded
        result = stateFix(result, match[1], result.user)
        if (setName) {
          if (!isNaN(setName)) {
            const index = parseInt(setName) - 1
            if (index >= 0 && index < result.profiles.length) {
              result.activeProfile = index
            }
          } else {
            const index = result.profiles.findIndex(
              (profile) => profile.name === setName
            )
            if (index >= 0) {
              result.activeProfile = index
            }
          }
        }
        await Store.instance.setState(result)
        const profile = result.profiles[result.activeProfile]
        if (profile.buildinfo?.text) {
          editingContext.dispatch({ type: 'buildinfo' })
        }
      },
      (error) => error.message
    )
  }

  await dataLoaded

  // uncomment this if you want to wait for profile load before rendering the page
  if (loading) await loading

  const session = new Session()
  const api = new Api(session.store)
  api.updateLoginFrontend()

  //history.subscribe(state => state, state => console.log(state));

  ReactDOM.render(
    <ErrorReporter>
      <App
        session={session}
        loading={loading}
        editingContext={editingContext}
      />
    </ErrorReporter>,
    document.querySelector('.d2planner-wrapper')
  )
})()
